import React, { useCallback, useRef } from "react";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "sonner";

import { GeoFieldBase } from "@/src/components/form-fields/GeoField";
import {
  RestrictionPeriodFieldBase,
  toTemporalRestrictionFields,
} from "@/src/components/form-fields/RestrictionPeriodField";

import SmartlistCastingsBlock from "./SmartlistCastingsBlock";
import SmartlistCriteriaFilter from "./SmartlistCriteriaFilter";
import SmartlistTranslations from "./SmartlistTranslations";
import SmartlistVariablesBlock from "./SmartlistVariablesBlock";
import { SmartlistType } from "../../api/MediaAPI.types";
import { CONTENT_MAX_WIDTH_STYLE } from "../../constants/size";
import "../../css/admin/utils/item-status.scss";
import i18n from "../../i18n";
import { getStorefrontUrl } from "../../utils/data/Project";
import ItemStatus from "../../utils/ItemStatus";
import {
  EMPTY_SMARTLIST_ITEM_LIST,
  mapContentsToSmartlistItems,
} from "../../utils/smartlist";
import { getDefaultSlug } from "../../utils/Translations";
import Block from "../block/Block";
import SelectBox from "../selectBox/SelectBox";
import { SELECT_BOX_TYPES } from "../selectBox/types/selectBoxTypes";
import Breadcrumbs from "../utils/Breadcrumbs";
import ContainerHeader from "../utils/ContainerHeader";

import type { ChangeEvent, Dispatch, JSX, SetStateAction } from "react";

import type { GeoSelection } from "@/src/components/form-fields/GeoField";
import type { RestrictionPeriodChange } from "@/src/components/form-fields/RestrictionPeriodField";

import type { SmartlistTranslationsHandle } from "./SmartlistTranslations";
import type {
  GeoblockMode,
  GetSmartlistResponse,
  SmartlistCasting,
  SmartlistItem,
  UpdatedSmartlist,
} from "../../api/SmartlistAPI.types";
import type { AppState } from "../../reducers";
import type { SelectBoxDisplayItem } from "../selectBox/selectBox.types";

const navigationHeaderLinks = {
  en: "https://support.okast.tv/en/support/solutions/articles/103000097751-how-to-configure-your-smartlists-pages",
  fr: "https://support.okast.tv/fr/support/solutions/articles/103000097751-configurer-ses-pages-de-smartlists",
};

const tabs = [
  { key: "contents", icon: "videocam", index: 0 },
  { key: "standard", icon: "folder_special", index: 1 },
  { key: "audios", icon: "queue_music", index: 2 },
  { key: "displays", icon: "video_library", index: 3 },
  { key: "castings", icon: "face", index: 4 },
  { key: "series", icon: "weekend", index: 5 },
];

interface SmartlistFormProps {
  smartlist: GetSmartlistResponse | UpdatedSmartlist;
  setSmartlist: Dispatch<SetStateAction<UpdatedSmartlist>>;
  onSubmit: (
    updatedSmartlist: UpdatedSmartlist,
    onSuccess?: () => void,
  ) => void;
  isLoading: boolean;
}

function SmartlistForm({
  smartlist,
  setSmartlist,
  onSubmit,
  isLoading,
}: SmartlistFormProps): JSX.Element {
  const { t } = useTranslation("editor-smartlist");

  const translationsRef = useRef<SmartlistTranslationsHandle>(null);

  const projects = useSelector(
    (state: AppState) => state.fetchReducer.projects,
  );
  const projectUuid = useSelector(
    (state: AppState) => state.architectureReducer.project_uuid,
  );

  const isAuto = smartlist.type === SmartlistType.AUTO;
  const isStandard = smartlist.type === SmartlistType.STANDARD;

  const handleLabelChange = (
    target: ChangeEvent<HTMLInputElement>["target"],
  ): void => {
    setSmartlist((currentSmartlist) => ({
      ...currentSmartlist,
      label: target.value,
    }));
  };

  const handleSmartlistItemsChange = useCallback(
    (contents: (SelectBoxDisplayItem | SmartlistItem)[]): void => {
      setSmartlist((currentSmartlist) => ({
        ...currentSmartlist,
        smartlist_items: mapContentsToSmartlistItems(contents),
      }));
    },
    [setSmartlist],
  );

  const handleGeoblockCountries = ({
    countries,
    countryGroups,
  }: GeoSelection): void => {
    setSmartlist((currentSmartlist) => ({
      ...currentSmartlist,
      countries,
      country_groups: countryGroups,
    }));
  };

  const handleGeoblockMode = (mode: GeoblockMode): void => {
    setSmartlist((currentSmartlist) => ({
      ...currentSmartlist,
      geoblock_mode: mode,
    }));
  };

  const handleTemporalRestriction = (change: RestrictionPeriodChange): void => {
    setSmartlist((currentSmartlist) => ({
      ...currentSmartlist,
      ...toTemporalRestrictionFields(change),
    }));
  };

  const handleStatusChange = (enabled: boolean): void => {
    setSmartlist((currentSmartlist) => ({
      ...currentSmartlist,
      enabled,
    }));
  };

  const handleVariablesChange = (variables: Record<string, string>): void => {
    setSmartlist((currentSmartlist) => ({
      ...currentSmartlist,
      custom_params: variables,
    }));
  };

  const handleCastingsChange = (castings: SmartlistCasting[]): void => {
    setSmartlist((currentSmartlist) => ({
      ...currentSmartlist,
      castings,
    }));
  };

  const handleSave = async (): Promise<void> => {
    const { translations, translations_indexes: translationsIndexes } =
      translationsRef.current?.getApiTranslations?.() ?? {
        translations: [],
        translations_indexes: {},
      };

    const isValidForm = (await translationsRef.current?.isValid()) ?? true;

    if (!isValidForm) {
      toast.error(t("form:validationError"));

      return;
    }

    const handleSuccess = (): void => translationsRef.current?.onSaveSuccess();

    onSubmit(
      {
        ...smartlist,
        translations,
        translations_indexes: translationsIndexes,
      } as UpdatedSmartlist,
      handleSuccess,
    );
  };

  const baseUrl = getStorefrontUrl(projectUuid, projects);
  const status = ItemStatus.getSmartlistStatus(smartlist);
  const defaultSlug = getDefaultSlug(smartlist.translations);

  const breadcrumbs = [
    { name: t("editor-menu:publishing-management"), url: "/smartlist" },
    { name: t("sectionTitle"), url: "/smartlist" },
    { name: t("edit") },
  ];

  return (
    <div
      className="editor-smartlist-form-container editor-has-breadcrumbs mx-auto flex max-w-(--maxWidth) flex-col gap-3.75"
      style={CONTENT_MAX_WIDTH_STYLE}
    >
      <Breadcrumbs items={breadcrumbs} />
      <ContainerHeader
        withBackBtn
        defaultLabel={t("unamed")}
        hasComments={smartlist.comments?.length}
        infoLinks={navigationHeaderLinks}
        item={smartlist}
        itemStatus={status}
        loading={isLoading}
        onChangeLabel={handleLabelChange}
        onChangeState={handleStatusChange}
        onSave={handleSave}
        previewDisabled={status !== "active"}
        previewLink={`${baseUrl}/smartlists/${defaultSlug ?? smartlist.uuid}`}
        type="smartlist"
      />
      <SmartlistTranslations
        ref={translationsRef}
        baseUrl={baseUrl}
        smartlist={smartlist}
      />
      {isStandard ? (
        <Block title={t("elements")}>
          <SelectBox
            ignoreSmartlistsAudios={false}
            items={smartlist.smartlist_items ?? EMPTY_SMARTLIST_ITEM_LIST}
            label={t("elementsSmartlist")}
            onChangeItems={handleSmartlistItemsChange}
            selectBoxType={SELECT_BOX_TYPES.SMARTLISTS}
            tabs={tabs}
          />
        </Block>
      ) : null}
      {isAuto ? (
        <Block className="z-10" title={t("filterCriteriaTitle")}>
          <SmartlistCriteriaFilter
            loading={isLoading}
            onChangeFilter={setSmartlist}
            smartlist={smartlist}
          />
        </Block>
      ) : null}
      <SmartlistCastingsBlock
        onChange={handleCastingsChange}
        value={smartlist.castings ?? []}
      />
      <Block title={t("restrictions")}>
        <div className="flex max-w-[500px] flex-col gap-8">
          <GeoFieldBase
            geoblockMode={smartlist.geoblock_mode}
            onModeChange={handleGeoblockMode}
            onSelectionChange={handleGeoblockCountries}
            selectedCountries={smartlist.countries ?? []}
            selectedCountryGroups={smartlist.country_groups ?? []}
          />
          <RestrictionPeriodFieldBase
            beginDate={smartlist.begin_date}
            checked={Boolean(smartlist.temporal_restriction)}
            endDate={smartlist.end_date}
            label={t("editor-smartlist:availibility")}
            lang={i18n.language}
            onChange={handleTemporalRestriction}
          />
        </div>
      </Block>
      <SmartlistVariablesBlock
        onChange={handleVariablesChange}
        value={smartlist.custom_params}
      />
    </div>
  );
}

export default SmartlistForm;
